import type { ColumnDef, ExpandedState, OnChangeFn, Row, SortingState, Updater } from "@tanstack/react-table"
import type { FC, RefObject } from "react"

import { useGoodsReceiptColumns } from "../../hooks"
import { getGoodsReceiptRoutePathFactory } from "../../utils"

import { BaseGenericTable } from "@/components/Tables/BaseGenericTable"
import type { GoodsReceipt, GoodsReceiptStatus } from "@/graphql/codegen/graphql"
import { useAppView } from "@/hooks/useAppView"

interface GoodsReceiptsTableProps {
  data: GoodsReceipt[]
  isLoading: boolean
  expandedState?: ExpandedState
  sortingState: SortingState
  tableContainerRef: RefObject<HTMLDivElement>
  status: GoodsReceiptStatus
  onExpandedChange?: (updaterOrValue: Updater<ExpandedState>) => void
  onSortingChange: OnChangeFn<SortingState>
  onEdit?: (goodsReceipt: GoodsReceipt) => void
  onDelete?: (goodsReceipt: GoodsReceipt) => void
  onPrint?: (goodsReceipt: GoodsReceipt) => void
  onMarkAsNotReceived?: (goodsReceipt: GoodsReceipt) => void
  onScroll: () => void
  renderExpandedContent: (row: Row<GoodsReceipt>) => React.ReactNode
  columns?: ColumnDef<GoodsReceipt>[]
}

export const GoodsReceiptsTable: FC<GoodsReceiptsTableProps> = ({
  data,
  isLoading,
  expandedState,
  sortingState,
  tableContainerRef,
  status,
  onEdit,
  onDelete,
  onPrint,
  onMarkAsNotReceived,
  onExpandedChange,
  onSortingChange,
  onScroll,
  renderExpandedContent,
  columns: customColumns,
}) => {
  const { isTabletView } = useAppView()
  const defaultColumns = useGoodsReceiptColumns({ onEdit, onDelete, onMarkAsNotReceived, onPrint, status })
  const columns = customColumns || defaultColumns

  return (
    <BaseGenericTable
      data={data}
      isLoading={isLoading}
      expandedState={expandedState}
      sortingState={sortingState}
      tableContainerRef={tableContainerRef}
      getRowId={(row: GoodsReceipt) => row.goodsReceiptId}
      onExpandedChange={onExpandedChange}
      onSortingChange={onSortingChange}
      onScroll={onScroll}
      renderExpandedContent={renderExpandedContent}
      columns={columns}
      getRoutePath={getGoodsReceiptRoutePathFactory(status, isTabletView)}
      noDataMessage='No goods receipts found'
    />
  )
}
